import { FloatingCTAButton } from "@/components/dashboard";
import { SecureLoader } from "@/components/dashboard/SecureLoader";
import { MvpDetailsBuyForm } from "@/components/mvp-order-summary/MvpDetailsBuyForm";
import { MvpDetailsSellForm } from "@/components/mvp-order-summary/MvpDetailsSellForm";
import { OrderSummary, SocialProof } from "@/components/order-summary";
import { TrustPilotRating } from "@/components/order-summary/TrustPilotRating.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  ALLOWED_DEAL_SUBMISSION_STAGES,
  BUSINESS_TYPES,
  buySubmissionFormEventDataSchema,
  DealCategory,
  MilesExpirationDate,
  PaymentMethod,
  ProductType,
  sellSubmissionFormEventDataSchema,
} from "@/core";
import {
  useFingerprint,
  useGetCustomerById,
  useGetDealById,
  useGetProductById,
} from "@/hooks";
import { resolveCustomerContact } from "@/lib/customer-contact";
import { calculateDynamicRate, MilesProgram } from "@/lib/miles-calculations";
import { serviceHost } from "@/services";
import { TransactionTypes, useUserCredentialsStore } from "@/stores";
import {
  buildCustomerPortalAnalyticsPayload,
  pushCustomerPortalAnalyticsEvent,
  savePendingDealSubmittedPayload,
} from "@/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

export function MVPOrderSummaryPage() {
  const { fingerprintData, isCollecting } = useFingerprint();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const hasTrackedPageOpenRef = useRef(false);

  const functionsService = serviceHost.getFunctionsService();

  const dealId = searchParams.get("quote") ?? "";
  const dealQuery = useGetDealById(dealId);

  const deal = useMemo(() => dealQuery, [dealQuery]);

  const product = useGetProductById(deal.data?.productId ?? "");

  useEffect(() => {
    if (dealQuery.isError || (!dealQuery.isLoading && !dealQuery.data)) {
      navigate("/quote-not-found");
      return;
    }

    if (
      dealQuery.data &&
      !ALLOWED_DEAL_SUBMISSION_STAGES.includes(dealQuery.data.stage)
    ) {
      navigate("/thank-you");
    }
  }, [dealQuery.isError, dealQuery.isLoading, dealQuery.data, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3500);

    return () => clearTimeout(timer);
  }, []);

  const transactionType = deal.data?.type;
  const customerId = deal.data?.customerId;
  const customerQuery = useGetCustomerById(customerId ?? "");
  const fallbackEmail = useUserCredentialsStore((state) => state.email);
  const fallbackFirstName = useUserCredentialsStore((state) => state.firstName);
  const fallbackLastName = useUserCredentialsStore((state) => state.lastName);
  const fallbackPhoneNumber = useUserCredentialsStore(
    (state) => state.phoneNumber,
  );
  const customerContact = resolveCustomerContact({
    customer: customerQuery.data,
    fallbackProfile: {
      email: fallbackEmail,
      phoneNumber: fallbackPhoneNumber,
    },
  });
  const isWaitingForCustomerContact =
    transactionType === BUSINESS_TYPES.SELL &&
    Boolean(customerId) &&
    !customerQuery.isFetched &&
    (!fallbackEmail || !fallbackPhoneNumber);

  useEffect(() => {
    if (hasTrackedPageOpenRef.current || !fingerprintData || isCollecting) {
      return;
    }

    if (!deal.data || !product.data) {
      return;
    }

    if (customerId && !customerQuery.isFetched) {
      return;
    }

    const payload = buildCustomerPortalAnalyticsPayload({
      event: "offer_view",
      customer: customerQuery.data,
      fallbackProfile: {
        email: fallbackEmail,
        firstName: fallbackFirstName,
        lastName: fallbackLastName,
        phoneNumber: fallbackPhoneNumber,
      },
      offerType: transactionType,
      milesPointsType: product.data.name || deal.data.productName,
      amount_of_miles: deal.data.quantity,
      amount: deal.data.amount,
      bonusTier: deal.data.statusTier,
    });

    pushCustomerPortalAnalyticsEvent({ payload });
    hasTrackedPageOpenRef.current = true;
  }, [
    customerId,
    customerQuery.data,
    customerQuery.isFetched,
    deal.data,
    fallbackEmail,
    fallbackFirstName,
    fallbackLastName,
    fallbackPhoneNumber,
    fingerprintData,
    isCollecting,
    product.data,
    transactionType,
  ]);

  // Early return after all hooks have been called
  if (!fingerprintData || isCollecting) {
    return <SecureLoader />;
  }

  const accountBuyDetails: Omit<
    z.infer<typeof buySubmissionFormEventDataSchema.shape.data>,
    "miles" | "averageRate" | "totalPayout"
  > = {
    firstName: "",
    lastName: "",
    frequentFlyerNumber: "",
    accountEmail: "",
    receivedMilesBefore: "",
    notes: "",
  };

  const accountSellDetails: Omit<
    z.infer<typeof sellSubmissionFormEventDataSchema.shape.data>,
    "miles" | "averageRate" | "totalPayout" | "paymentMethod"
  > & {
    paymentMethod?: PaymentMethod;
  } = {
    accountEmail: customerContact.email,
    frequentFlyerNumber: "",
    accountPhoneNumber: customerContact.phoneNumber,
    milesExpirationDate: MilesExpirationDate.THREE_MONTHS,
    accountPassword: "",
    notes: "",
  };

  const program: MilesProgram = {
    id: product.data?.id ?? "",
    name: product.data?.name ?? "",
    category: product.data?.category ?? DealCategory.AirlineMiles,
    logo:
      product.data?.logo === "" || !product.data?.logo
        ? "/tmm-dark-logo.svg"
        : product.data.logo,
    inactive: product.data?.inactive ?? false,
    buyOrSell: product.data?.buyOrSell ?? ProductType.BOTH,
    minimumValueForSell: product.data?.minimumValueForSell ?? 0,
    fingerprint: fingerprintData,
    maximumValueForSell: product.data?.maximumValueForSell ?? 0,
    minimumValueForBuy: product.data?.minimumValueForBuy ?? 0,
    maximumValueForBuy: product.data?.maximumValueForBuy ?? 0,
    sellPrices: product.data?.sellPrices ?? [],
    buyPrices: product.data?.buyPrices ?? [],
    statusTiers: product.data?.statusTiers ?? [],
    usernameLabelOverride: product.data?.usernameLabelOverride,
    emailLabelOverride: product.data?.emailLabelOverride,
    passwordLabelOverride: product.data?.passwordLabelOverride,
    programId: product.data?.id + "-" + Date.now().toString(),
    airline: product.data?.name ?? "",
    loyaltyStatus: deal.data?.statusTier ?? null,
    statusBonus: 0,
    totalPayout: deal.data?.amount ?? 0,
    promoCode: deal.data?.promoCode,
    ratePerMile: calculateDynamicRate({
      miles: deal?.data?.quantity ?? 0,
      transactionType:
        (transactionType as TransactionTypes) ?? TransactionTypes.SELL,
      sellPrices: product.data?.sellPrices ?? [],
      buyPrices: product.data?.buyPrices ?? [],
      fallbackRate: 0,
    }),
    sanitizedMiles: deal?.data?.quantity ?? 0,
    accountDetails:
      transactionType === BUSINESS_TYPES.BUY
        ? accountBuyDetails
        : accountSellDetails,
  };

  const onContinueClick = async () => {
    if (isFormSubmitting || !deal.data || !product.data) {
      return;
    }

    setSubmissionError(null);
    setIsFormSubmitting(true);

    try {
      if (transactionType === BUSINESS_TYPES.BUY) {
        const buyAccountDetails = program.accountDetails as Omit<
          z.infer<typeof buySubmissionFormEventDataSchema.shape.data>,
          "miles" | "averageRate" | "totalPayout"
        >;
        await functionsService.createBuyOrderMVP({
          productId: program.id,
          customerId: customerId ?? "",
          loyaltyStatus: program.loyaltyStatus?.name ?? "",
          miles: program.sanitizedMiles,
          accountEmail: buyAccountDetails.accountEmail,
          firstName: buyAccountDetails.firstName,
          lastName: buyAccountDetails.lastName,
          frequentFlyerNumber: buyAccountDetails.frequentFlyerNumber,
          receivedMilesBefore: buyAccountDetails.receivedMilesBefore,
          notes: buyAccountDetails.notes ?? "",
          fingerprint: fingerprintData,
        });
      }

      if (transactionType === BUSINESS_TYPES.SELL) {
        const sellAccountDetails = program.accountDetails as Omit<
          z.infer<typeof sellSubmissionFormEventDataSchema.shape.data>,
          "miles" | "averageRate" | "totalPayout"
        >;

        const basePayload = {
          productId: program.id,
          customerId: customerId ?? "",
          loyaltyStatus: program.loyaltyStatus?.name ?? "",
          miles: program.sanitizedMiles,
          accountEmail:
            customerContact.email || sellAccountDetails.accountEmail,
          frequentFlyerNumber: sellAccountDetails.frequentFlyerNumber,
          accountPhoneNumber:
            customerContact.phoneNumber ||
            sellAccountDetails.accountPhoneNumber,
          milesExpirationDate: sellAccountDetails.milesExpirationDate,
          accountPassword: sellAccountDetails.accountPassword ?? "",
          paymentMethod: sellAccountDetails.paymentMethod,
          dealId,
          fingerprint: fingerprintData,
        };

        const payload =
          sellAccountDetails.paymentMethod === PaymentMethod.PAYPAL
            ? { ...basePayload, paypalEmail: sellAccountDetails.paypalEmail }
            : { ...basePayload, notes: sellAccountDetails.notes ?? "" };

        await functionsService.createSellOrderMVP(payload);
      }

      const trackingPayload = buildCustomerPortalAnalyticsPayload({
        event: "deal_add_payout_method",
        customer: customerQuery.data,
        fallbackProfile: {
          email: fallbackEmail,
          firstName: fallbackFirstName,
          lastName: fallbackLastName,
          phoneNumber: fallbackPhoneNumber,
        },
        offerType: transactionType,
        milesPointsType: product.data.name || deal.data.productName,
        amount_of_miles: program.sanitizedMiles,
        amount: program.totalPayout,
        payoutMethod:
          transactionType === BUSINESS_TYPES.SELL
            ? (
                program.accountDetails as Omit<
                  z.infer<typeof sellSubmissionFormEventDataSchema.shape.data>,
                  "miles" | "averageRate" | "totalPayout"
                >
              ).paymentMethod
            : null,
        bonusTier: program.loyaltyStatus,
      });

      pushCustomerPortalAnalyticsEvent({ payload: trackingPayload });

      savePendingDealSubmittedPayload({
        payload: buildCustomerPortalAnalyticsPayload({
          event: "final_deal_submitted",
          customer: customerQuery.data,
          fallbackProfile: {
            email: fallbackEmail,
            firstName: fallbackFirstName,
            lastName: fallbackLastName,
            phoneNumber: fallbackPhoneNumber,
          },
          offerType: transactionType,
          milesPointsType: product.data.name || deal.data.productName,
          amount_of_miles: program.sanitizedMiles,
          amount: program.totalPayout,
          payoutMethod:
            transactionType === BUSINESS_TYPES.SELL
              ? (
                  program.accountDetails as Omit<
                    z.infer<
                      typeof sellSubmissionFormEventDataSchema.shape.data
                    >,
                    "miles" | "averageRate" | "totalPayout"
                  >
                ).paymentMethod
              : null,
          bonusTier: program.loyaltyStatus,
          dealStatus: "successful",
        }),
      });

      navigate("/thank-you");
    } catch (error) {
      console.error("Error submitting MVP order details:", error);
      setSubmissionError(
        error instanceof Error
          ? error.message
          : "We couldn't submit your order. Please try again.",
      );
    } finally {
      setIsFormSubmitting(false);
    }
  };

  if (isLoading || isWaitingForCustomerContact) {
    return <SecureLoader />;
  }

  return (
    <div className="relative flex flex-col gap-6 md:gap-0 w-full justify-between px-4 md:px-6 py-12 bg-[#063e9f]">
      <FloatingCTAButton offersRef={formRef} />
      {/*<div*/}
      {/*  className={`absolute max-h-[500px] top-0 left-0 w-full ${transactionType === BUSINESS_TYPES.SELL ? "h-[80%]" : "h-[60%] md:h-[50%]"} bg-[#063e9f]`}*/}
      {/*/>*/}

      <div
        className={`w-full max-w-[1500px] mx-auto flex justify-center flex-col xl:flex-row gap-6 xl:gap-12 pt-[2%]`}
      >
        <div
          className={`w-full flex flex-col items-center xl:w-[45%] gap-6 z-10`}
        >
          {/* Title and Subtitle Section */}
          <div
            className={`text-center xl:text-left relative ${
              transactionType === BUSINESS_TYPES.SELL ? "hidden xl:block" : ""
            }`}
          >
            {/* Background accent shape */}
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-cyan-300/10 rounded-full blur-xl"></div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 relative z-10">
              Just one more step to{" "}
              <span className="relative inline-block">
                <span className="relative text-white  font-black drop-shadow-[0_2px_8px_rgba(5,102,255,0.2)] filter contrast-110 brightness-105">
                  {transactionType === BUSINESS_TYPES.SELL
                    ? "get paid for your miles"
                    : "secure your miles"}
                </span>
              </span>
            </h1>

            {/* <div className="relative">
              <p className="relative text-lg md:text-xl font-medium text-white py-3">
                Fill in your final details — secure, fast, and you'll get paid
                today.
              </p>
            </div> */}
          </div>

          <div className="w-full flex flex-col gap-4">
            <OrderSummary
              program={program}
              showPayoutClaim={transactionType === BUSINESS_TYPES.SELL}
            />
          </div>

          {/* <div className="block xl:hidden w-full">
            <Offer formRef={formRef} />
          </div> */}

          <div className="hidden xl:block">
            <TrustPilotRating />
            <SocialProof />
          </div>
        </div>
        <div className="flex flex-col gap-6 w-full xl:max-w-[60%] z-10">
          <div ref={formRef} id="details-form">
            {submissionError ? (
              <Alert variant="destructive" className="mb-4">
                <AlertTitle>Submission failed</AlertTitle>
                <AlertDescription>{submissionError}</AlertDescription>
              </Alert>
            ) : null}
            {transactionType === BUSINESS_TYPES.SELL ? (
              <MvpDetailsSellForm
                onContinueClick={onContinueClick}
                program={program}
                isFormSubmitting={isFormSubmitting}
                customerEmail={customerContact.email}
                customerPhoneNumber={customerContact.phoneNumber}
              />
            ) : (
              <MvpDetailsBuyForm
                onContinueClick={onContinueClick}
                program={program}
                isFormSubmitting={isFormSubmitting}
              />
            )}
          </div>
        </div>

        <div className="block xl:hidden">
          <TrustPilotRating />
          <SocialProof />
        </div>
      </div>
    </div>
  );
}
